<template>
    <activity-booking-page-layout
        v-if="loaded && offer"
        class="activity-booking-page"
        :search-request="searchRequest"
        :offer="offer"
        :product="product"
        :product-store="productStore"
        :search-request-country-id="searchRequestCountryId"
        tourist-passport-form
    >
        <template v-slot:breadcrumbs>
            <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="refreshExpiredOffers" />
            <activity-breadcrumbs :search-request="searchRequest" :product="product" />
            <page-title :text="$t('page_titles.activity_booking_page_title')" />
        </template>
        <template
            v-if="showActivityBookingForm"
            v-slot:forms="{bookingKey, basketKey, formRefs, isSavingWithoutDetails}"
        >
            <v-card variant="outlined" class="activity-booking-page__point mb-5">
                <v-card-title>
                    <v-icon start color="primary">mdi-map-marker</v-icon>
                    {{
                        product.productType.toLowerCase() === 'event'
                            ? $t('activities.time_and_location')
                            : $t('activities.pick-up_point_and_time')
                    }}
                </v-card-title>
                <v-divider />
                <v-card-text>
                    <activity-booking-form
                        :ref="el => formRefs.push(el)"
                        :offer="offer"
                        :start-time="offer.startTimes[0]"
                        :info="basketItem.info"
                        :disabled="!!bookingKey || !!basketKey"
                        :pickup-location="selectedOrderFirstHotel"
                        :disable-required-rule="isSavingWithoutDetails"
                        @change="setPickupPointField"
                    />
                </v-card-text>
            </v-card>
        </template>
        <template v-slot:orderCommentsForm="{bookingKey, basketKey, formRefs}">
            <booking-comments-form
                :ref="el => formRefs.push(el)"
                :disabled="!!bookingKey || !!basketKey"
                :product-store="productStore"
            />
            <template v-if="isShowSeatMap">
                <transfer-seats-map
                    v-model="productStore.seats"
                    :store-index="0"
                    :prepare-book-request="prepareBookRequest"
                    :offer="offer"
                    :options="productStore.bookingAdditionalOptions"
                    :disabled="!!bookingKey"
                    :search-request="searchRequest"
                    :tourists="tourists"
                />
            </template>
        </template>
        <template v-slot:orderSummary="{bookingKey, basketKey, selectedAddOns, extraServices}">
            <activity-order-summary
                :product="product"
                :offers="[offer]"
                :info="basketItem.info"
                :prebooked="!!bookingKey || !!basketKey"
                :selected-add-ons="selectedAddOns"
                :search-request="searchRequest"
                :extra-services="extraServices"
                :additional-options="productStore.bookingAdditionalOptions"
                :is-show-seats="true"
            >
                <template v-slot:summary>
                    <v-card flat rounded="0" class="activity-booking-page__summary-card my-2 pa-0">
                        <v-card-text class="pa-0">
                            <search-summary-content
                                :search-request="searchRequest"
                                :date-prop="offer.date"
                                tourists-label="guest"
                                show-date
                                show-tourists
                            />
                        </v-card-text>
                    </v-card>
                </template>
            </activity-order-summary>
        </template>
        <conditions-modal>
            <template v-slot:info>
                <transfer-conditions-modal-info />
                <activity-conditions-modal-info />
                <hotel-conditions-modal-info />
            </template>
        </conditions-modal>
        <hotel-meal-type-info-modal />
        <hotel-room-info-modal />
        <additional-fees-modal />
        <transfer-info-modal />
        <activity-info-modal />
    </activity-booking-page-layout>
</template>

<script lang="ts">
    import {Component, mixins, Provide} from 'vue-facing-decorator'
    import ActivityBreadcrumbs from '@/components/activities/ActivityBreadcrumbs.vue'
    import ActivityOrderSummary from '@/components/activities/booking/ActivityOrderSummary.vue'
    import ActivityBookingPageLayout from '~/components/activities/booking/ActivityBookingPageLayout.vue'
    import {ActivityStoreMixin} from '@/mixins/ActivityStoreMixin.vue'
    import {ActivityPageMixin} from '@/components/activities/mixins/ActivityPageMixin.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import {clone, UTCToDate} from '@/utils/helpers'
    import BookingCommentsForm from '~src/components/booking/forms/bookingCommentsForm.src'
    import SearchSummaryContent from '@/components/parts/SearchSummaryContent.vue'
    import ActivityBookingForm from '@/components/activities/booking/forms/ActivityBookingForm.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import TransferConditionsModalInfo from '@/components/transfers/snippets/TransferConditionsModalInfo.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import HotelConditionsModalInfo from '@/components/hotels/snippets/HotelConditionsModalInfo.vue'
    import HotelPriceNote from '@/components/hotels/snippets/HotelPriceNote.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'
    import HotelMealTypeInfoModal from '@/components/hotels/modals/HotelMealTypeInfoModal.vue'
    import HotelRoomInfoModal from '@/components/modals/HotelRoomInfoModal.vue'
    import AdditionalFeesModal from '@/components/modals/AdditionalFeesModal.vue'
    import TransferInfoModal from '@/components/transfers/info/TransferInfoModal.vue'
    import {ProductBookingFieldsMixin} from '@/components/booking/mixins/ProductBookingFieldsMixin.vue'
    import TransferSeatsMap from '@/components/transfers/booking/TransferSeatsMap.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'

    @Component({
        components: {
            TransferSeatsMap,
            TransferInfoModal,
            AdditionalFeesModal,
            HotelRoomInfoModal,
            HotelMealTypeInfoModal,
            ActivityInfoModal,
            HotelPriceNote,
            HotelConditionsModalInfo,
            ActivityConditionsModalInfo,
            TransferConditionsModalInfo,
            ConditionsModal,
            ActivityBookingForm,
            ExpiredOffersSnackbar,
            ActivityBreadcrumbs,
            ActivityOrderSummary,
            ActivityBookingPageLayout,
            BookingCommentsForm,
            SearchSummaryContent,
            PageTitle,
        },
    })
    export class ActivityBookingPage extends mixins(ActivityStoreMixin, ActivityPageMixin, ProductBookingFieldsMixin) {
        loaded = false
        selectedOrderFirstHotel = null

        async mounted() {
            await this.$store.restored
            await this.loadRuntimeData()
            await runtimeStore.loadTouristBookingFields({offerKey: this.$route.query.offerKey})
            await runtimeStore.loadProductBookingFields({offerKey: this.$route.query.offerKey})
            this.productStore.clearBookingAdditionalOptions()
            this.clearPickupPointFields()
            this.prefillPickupPointFields()
            await this.productStore.getExtraServiceAdditionalOptions(this.$route.query.offerKey)
            this.loaded = true
        }

        clearPickupPointFields() {
            this.setPickupPointField({prop: 'startTime', val: null})
            this.setPickupPointField({prop: 'hotelPointKey', val: null})
            this.setPickupPointField({prop: 'pickUpPlace', val: null})

            this.selectedOrderFirstHotel = null
        }

        prefillPickupPointFields() {
            const startTime = this.offer.startTimes?.[0]
            const language = this.offer.languages?.[0] || null

            if (startTime) {
                this.setPickupPointField({prop: 'startTime', val: startTime})
            }
            if (language) {
                this.setPickupPointField({prop: 'language', val: language})
            }

            if (persistentStore.selectedOrderId) {
                const hotelName = runtimeStore.orderDetails?.services?.find(s => s.serviceType === 'ACCOMMODATION')
                    ?.serviceDetails?.[0]?.hotelName
                this.selectedOrderFirstHotel = hotelName ? {name: hotelName} : null
            }
        }

        setPickupPointField({prop, val}) {
            const rqCopy = clone(this.prepareBookRequest)

            if (!val) {
                delete rqCopy[prop]
            }

            this.productStore.SET_PREPARE_BOOK_REQUEST({
                ...rqCopy,
                ...(val && {[prop]: val}),
            })
        }

        isOffersExpired() {
            return (
                this.$dateFns.isAfter(new Date(), UTCToDate(this.$dateFns.parseISO(this.basketItem.expirationTime))) &&
                !runtimeStore.bookingActive &&
                !this.productStore.prepareBookResponse.bookingKey
            )
        }

        refreshExpiredOffers() {
            this.$router.push(this.productRuntimeStore.searchPageLink(this.searchRequest))
        }

        get prepareBookRequest() {
            return this.productStore.prepareBookRequest
        }

        get searchRequestCountryId() {
            return this.productRuntimeStore.city.countryId
        }

        get basketItem() {
            return this.productStore.basketItem(this.$route.query.offerKey)
        }

        @Provide get searchRequest() {
            return this.basketItem.searchRequest
        }

        get product() {
            return this.basketItem.product
        }

        get offer() {
            return this.product.offers.find(offer => offer.offerKey === this.$route.query.offerKey)
        }

        get isShowSeatMap() {
            if (!this.isTO1 || !runtimeStore.getCRMOption('asp.option.ownExcursions.show.seats')) return false
            if (this.product.transportationType !== 'OFF_SITE') return false
            const isPriceExist = !!this.productStore.bookingAdditionalOptions?.seatSelectionServices?.[0]?.price
            if (!isPriceExist) this.productStore.seats = [[], []]
            return isPriceExist
        }

        get isTO1() {
            return authStore.isTO1
        }

        get showActivityBookingForm() {
            return (
                !this.isProductFieldDisabled('PICK_UP_HOTEL') ||
                !this.isProductFieldDisabled('PICK_UP_PLACE') ||
                !this.isProductFieldDisabled('PICK_UP_TIME')
            )
        }

        get tourists() {
            return persistentStore.tourists
        }
    }

    export default ActivityBookingPage
</script>
